/*
 * interviewData — the Interviews workspace's shared catalogue and helpers.
 *
 * Roles map onto REAL Knowledge Hub scope (rootIds): that scope is what the
 * existing interview engine builds questions from (start-session scope_ids for
 * AI interviews, /engine/theory/next for written ones). Roles with no question
 * content in the hub are listed but not startable — the app never pretends to
 * interview you on material it doesn't have.
 *
 * Interview history lives in this browser (localStorage): the backend keeps no
 * per-interview history, so the frontend records each finished interview with
 * its setup and report.
 */
import { indexTree } from './paths';

export const ROLE_CATEGORIES = [
  ['popular', 'Popular'], ['ai', 'AI & ML'], ['eng', 'Engineering'], ['data', 'Data'], ['product', 'Product & Business'],
];

// family → which seniority ladder applies
export const ROLES = [
  { id: 'ml_engineer', title: 'ML Engineer', cats: ['popular', 'ai', 'eng'], family: 'eng', rootIds: ['ml', 'dl'] },
  { id: 'ai_engineer', title: 'AI / GenAI Engineer', cats: ['popular', 'ai', 'eng'], family: 'eng', rootIds: ['ai.rag', 'ai.llm', 'ai.ag', 'ai.pr'] },
  { id: 'data_scientist', title: 'Data Scientist', cats: ['popular', 'ai', 'data'], family: 'data', rootIds: ['ml', 'dl'] },
  { id: 'software_engineer', title: 'Software Engineer', cats: ['popular', 'eng'], family: 'eng', rootIds: ['dsa'] },
  { id: 'ai_product_manager', title: 'AI Product Manager', cats: ['popular', 'ai', 'product'], family: 'product', rootIds: ['ai.llm', 'ai.rag', 'ai.ag', 'ai.sg', 'ai.pr'] },
  { id: 'nlp_engineer', title: 'NLP Engineer', cats: ['ai', 'eng'], family: 'eng', rootIds: ['ai.nlp', 'ai.tok', 'ai.tf', 'ai.em', 'ai.llm'] },
  { id: 'dl_engineer', title: 'Deep Learning Engineer', cats: ['ai', 'eng'], family: 'eng', rootIds: ['dl', 'ai.tf'] },
  { id: 'research_scientist', title: 'Research Scientist', cats: ['ai'], family: 'eng', rootIds: ['ml', 'dl', 'ai.tf', 'ai.llm'] },
  { id: 'mlops_engineer', title: 'MLOps Engineer', cats: ['ai', 'eng'], family: 'eng', rootIds: ['ml.mt', 'ml.me', 'ml.mm', 'ai.ragp'] },
  { id: 'data_analyst', title: 'Data Analyst', cats: ['data'], family: 'data', rootIds: ['ml.sl', 'ml.me', 'ml.ul'] },
  { id: 'allrounder', title: 'Technical All-Rounder', cats: ['eng'], family: 'eng', rootIds: ['dsa', 'ml'] },
  // No question content in the hub yet — shown, not startable.
  { id: 'product_manager', title: 'Product Manager', cats: ['popular', 'product'], family: 'product', rootIds: [] },
  { id: 'business_analyst', title: 'Business Analyst', cats: ['product', 'data'], family: 'data', rootIds: [] },
  { id: 'ux_researcher', title: 'UX Researcher', cats: ['product'], family: 'product', rootIds: [] },
  { id: 'project_manager', title: 'Project Manager', cats: ['product'], family: 'product', rootIds: [] },
  { id: 'product_designer', title: 'Product Designer', cats: ['product'], family: 'product', rootIds: [] },
  { id: 'consultant', title: 'Consultant', cats: ['product'], family: 'product', rootIds: [] },
];

export const LEVELS = ['Entry', 'Junior', 'Mid-level', 'Senior', 'Lead', 'Manager'];

export const COMPANIES = ['Google', 'Microsoft', 'Amazon', 'Meta', 'Apple', 'Netflix', 'Uber', 'OpenAI', 'NVIDIA', 'Salesforce'];
export const typeLabelLong = (t) => (TYPES.find((x) => x.id === t) || { label: 'Written interview' }).label;

export const TYPES = [
  { id: 'video', label: 'Video call', short: 'AI Video', blurb: 'Camera on, like a real interview call.' },
  { id: 'voice', label: 'Voice call', short: 'AI Voice', blurb: 'Audio only — your camera stays off.' },
  { id: 'written', label: 'Written Interview', short: 'Written', blurb: 'Read each question and write your answer.' },
];
export const isAiType = (t) => t === 'voice' || t === 'video';

export const DIFFICULTIES = [[null, 'Adaptive'], ['easy', 'Easy'], ['medium', 'Medium'], ['hard', 'Hard']];
export const diffLabel = (d) => (DIFFICULTIES.find(([v]) => v === d) || DIFFICULTIES[0])[1];

// Written interviews honour length; an AI interview is always the engine's 10 questions.
export const LENGTHS = [
  { id: 'quick', label: 'Quick', questions: 4, minutes: 10 },
  { id: 'standard', label: 'Standard', questions: 6, minutes: 20 },
  { id: 'deep', label: 'Deep', questions: 8, minutes: 40 },
];
export const AI_QUESTIONS = 10;
// The AI interviewer's question pack is a fixed size on the server today; 15 and 20 need a backend change.
export const AI_COUNTS = [{ n: 10, available: true, minutes: 20 }, { n: 15, available: false, minutes: 30 }, { n: 20, available: false, minutes: 40 }];
export const AI_MINUTES = 20;

// Interviewer personas — how the interviewer LOOKS. The voice is set by the server.
export const PERSONAS = [
  { id: 'professional', label: 'Professional', note: 'Calm and structured' },
  { id: 'friendly', label: 'Friendly', note: 'Warm and encouraging' },
  { id: 'formal', label: 'Formal', note: 'Crisp and to the point' },
  { id: 'executive', label: 'Senior Executive', note: 'Big-picture, high bar' },
  { id: 'technical', label: 'Technical', note: 'Detail-oriented' },
];
export const personaOf = (id) => PERSONAS.find((p) => p.id === id) || PERSONAS[0];

export const roleOf = (id) => ROLES.find((r) => r.id === id) || null;

/** Role scope restricted to hub nodes that exist. */
export const roleScope = (role, byId) => (role?.rootIds || []).filter((id) => !byId || byId[id]);

/** Focus options for a role: each root's topics (or the node itself when it is already a topic). */
export const focusOptions = (role, hubTree) => {
  const byId = indexTree(hubTree || []);
  const out = [];
  roleScope(role, hubTree ? byId : null).forEach((id) => {
    const n = byId[id];
    if (!n) return;
    const isRoot = !String(id).includes('.');
    (isRoot ? (n.children || []) : [n]).forEach((c) => out.push({ id: c.id, name: c.name }));
  });
  return out;
};

export const DEFAULT_SETUP = {
  roleId: 'ml_engineer', level: 'Mid-level', company: '', type: 'video',
  focus: [], recommended: true, difficulty: null, length: 'standard', persona: 'professional',
  questions: AI_QUESTIONS, personalize: true,
};

/** The hub ids an interview draws on: chosen focus topics, else the whole role scope. */
export const setupScope = (setup, byId) => {
  if (!setup.recommended && setup.focus.length) return setup.focus.map((f) => f.id);
  return roleScope(roleOf(setup.roleId), byId);
};

export const typeLabel = (t) => ({ voice: 'AI Voice', video: 'AI Video', written: 'Written', rapidfire: 'Rapid fire', library: 'Library interview' }[t] || 'AI Interview');
export const historyFilterOf = (t) => (t === 'written' ? 'written' : 'ai');

/* ── history (this browser) ─────────────────────────────────────────────── */
const HKEY = 'pu_interviews_v1';
const keyFor = (uid) => `${HKEY}:${uid || 'local'}`;
export const loadHistory = (uid) => {
  try { const v = JSON.parse(localStorage.getItem(keyFor(uid)) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
};
export const addHistory = (uid, entry) => {
  const list = [entry, ...loadHistory(uid).filter((e) => e.id !== entry.id)].slice(0, 25);
  try { localStorage.setItem(keyFor(uid), JSON.stringify(list)); } catch {
    // storage full — keep the newest few without their transcripts
    try { localStorage.setItem(keyFor(uid), JSON.stringify(list.slice(0, 8))); } catch { /* private mode */ }
  }
  return list;
};

export const reportScore = (r) => {
  if (!r || r.error) return null;
  const v = r.overall_score ?? r.metrics?.average_score;
  return v == null ? null : Math.round(v);
};
export const reportStatus = (r) => (!r ? 'Not scored' : r.error ? 'Not scored' : r.degraded ? 'Placeholder score' : r.ended_early ? 'Ended early' : 'Completed');

export const ago = (ts) => {
  const d = (Date.now() - Date.parse(ts)) / 86400000;
  if (!(d >= 0)) return '';
  if (d < 1 / 24) return 'just now';
  if (d < 1) return `${Math.round(d * 24)} h ago`;
  if (d < 2) return 'yesterday';
  if (d < 14) return `${Math.round(d)} days ago`;
  return `${Math.round(d / 7)} weeks ago`;
};

/** Topic scores from a report: the evaluator's concept scores, else question topics. */
const topicScores = (r) => {
  const cp = (r?.concept_performance || []).filter((c) => c.concept).map((c) => ({ name: c.concept, score: c.average_score }));
  if (cp.length) return cp;
  const by = {};
  (r?.question_evaluations || []).forEach((q, i) => {
    const name = q.topic || r?.questions_meta?.[i]?.topic;
    if (!name || q.status === 'skipped') return;
    by[name] = by[name] || { name, sum: 0, n: 0 };
    by[name].sum += q.score || 0; by[name].n += 1;
  });
  return Object.values(by).map((t) => ({ name: t.name, score: t.sum / t.n }));
};

/** One readable line about how an interview went — never a bare number. */
export const takeawayOf = (r) => {
  const score = reportScore(r);
  if (score == null) return r?.error ? 'Report unavailable' : 'Not scored';
  const ts = topicScores(r).sort((a, b) => a.score - b.score);
  const weak = ts[0] && ts[0].score < 70 ? ts[0].name : null;
  if (score >= 80) return weak ? `Strong overall · deepen ${weak}` : 'Strong fundamentals';
  if (score >= 60) return weak ? `Good base · improve ${weak}` : 'Solid answers, room to deepen';
  return weak ? `Needs work on ${weak}` : 'Needs more practice';
};
export const scoreText = (s) => (s == null ? '—' : `${s}/100`);

/** Goal (Home's goal id) → interview role. */
const GOAL_ROLE = { 'role.ml_engineer': 'ml_engineer', 'role.genai_engineer': 'ai_engineer', 'role.interview_allrounder': 'allrounder' };
export const roleForGoal = (goalId) => {
  if (!goalId) return null;
  if (GOAL_ROLE[goalId]) return roleOf(GOAL_ROLE[goalId]);
  return ROLES.filter((r) => r.rootIds.length)
    .find((r) => r.rootIds.some((id) => goalId === id || goalId.startsWith(`${id}.`) || id.startsWith(`${goalId}.`))) || null;
};

/*
 * Recommended focus areas for a role — each with the reason it was picked,
 * from real signals only: last interview feedback, accuracy, due reviews,
 * time since practice. Without memory it falls back to the role's core topics.
 */
export const recommendFocus = ({ role, hub, memTree, due = [], history = [], max = 4 }) => {
  const opts = focusOptions(role, hub);
  if (!opts.length) return [];
  const mem = indexTree(memTree || []);
  const topicOfId = (cid) => opts.find((o) => cid === o.id || String(cid).startsWith(`${o.id}.`));
  // topics the last interview for this role flagged (scored < 70)
  const last = history.find((e) => e.setup?.roleId === role?.id && e.report);
  const flagged = new Set();
  (last?.report?.question_evaluations || []).forEach((q, i) => {
    if ((q.score || 0) >= 70) return;
    const cid = (q.concept_ids || [])[0] || last.report.questions_meta?.[i]?.concept;
    const t = cid && topicOfId(cid);
    if (t) flagged.add(t.id);
  });
  const dueTopics = new Set(due.map((d) => topicOfId(d.concept_id)?.id).filter(Boolean));
  const pct = (v) => `${Math.round(v * 100)}%`;
  const scored = opts.map((o) => {
    const m = mem[o.id];
    if (flagged.has(o.id)) return { ...o, rank: 0, reason: 'Flagged in your last interview' };
    if (m?.attempts > 0 && (m.status === 'weak' || (m.mastery || 0) < 0.5)) {
      const acc = m.attempts >= 3 ? `${pct(m.correct / m.attempts)} accuracy` : `${pct(m.mastery || 0)} memory strength`;
      return { ...o, rank: 1 + (m.mastery || 0), reason: `Weak area · ${acc}` };
    }
    if (dueTopics.has(o.id)) return { ...o, rank: 3, reason: 'Due for review in your learning memory' };
    if (m?.attempts > 0 && (m.last_seen_days || 0) >= 14) return { ...o, rank: 4, reason: `Not practised in ${Math.round(m.last_seen_days)} days` };
    if (!(m?.attempts > 0)) return { ...o, rank: 5, reason: `Core ${role.title} topic · not practised yet` };
    return { ...o, rank: 9, reason: `${pct(m.mastery || 0)} memory strength` };
  });
  return scored.sort((a, b) => a.rank - b.rank).slice(0, max);
};

/** Build a history entry from a finished interview. */
export const makeEntry = ({ kind, setup, report, title }) => ({
  id: `iv_${Date.now().toString(36)}`,
  ts: new Date().toISOString(),
  kind,
  title: title || roleOf(setup?.roleId)?.title || (kind === 'rapidfire' ? 'Rapid-fire round' : 'AI Interview'),
  setup: setup || null,
  score: reportScore(report),
  status: reportStatus(report),
  takeaway: takeawayOf(report),
  report,
});

/* ── written interview → the same report shape the voice evaluator returns ── */
export const writtenReport = ({ items, planned, nameOf, durationSeconds }) => {
  const answered = items.filter((it) => it.grade);
  const evals = items.map((it, i) => ({
    index: i + 1,
    question: it.q.question,
    answer: it.answer || '',
    score: it.grade ? it.grade.score : 0,
    status: it.grade ? (it.grade.score >= 70 ? 'correct' : 'wrong') : 'skipped',
    strengths: it.grade?.strengths || [],
    improvements: it.grade?.gaps || [],
    key_points: it.grade?.key_points || [],
    concept_ids: it.q.concept_ids || [],
    topic: nameOf((it.q.concept_ids || [])[0]),
  }));
  const avg = answered.length ? answered.reduce((s, it) => s + it.grade.score, 0) / answered.length : 0;
  const byConcept = {};
  evals.filter((e) => e.status !== 'skipped').forEach((e) => {
    const k = e.topic || 'General';
    byConcept[k] = byConcept[k] || { concept: k, sum: 0, n: 0, concept_id: e.concept_ids[0] };
    byConcept[k].sum += e.score; byConcept[k].n += 1;
  });
  const concepts = Object.values(byConcept).map((c) => ({ concept: c.concept, concept_id: c.concept_id, average_score: c.sum / c.n, total_questions: c.n }));
  const sorted = [...concepts].sort((a, b) => b.average_score - a.average_score);
  const outOf = Math.max(planned || 0, items.length);
  const skipped = outOf - answered.length;
  const summary = answered.length
    ? `You answered ${answered.length} of ${outOf} questions${skipped ? ` (${skipped} skipped or not reached)` : ''}.`
      + (sorted.length > 1 ? ` Strongest: ${sorted[0].concept} (${Math.round(sorted[0].average_score)}%). Needs the most work: ${sorted[sorted.length - 1].concept} (${Math.round(sorted[sorted.length - 1].average_score)}%).` : '')
    : 'No answers were submitted, so there is nothing to score yet.';
  return {
    written: true,
    overall_score: avg,
    overall_assessment: summary,
    metrics: {
      average_score: avg, total_questions: outOf, attempted_questions: answered.length,
      correct_answers: answered.filter((it) => it.grade.score >= 70).length,
      total_time: durationSeconds, average_time_per_question: answered.length ? durationSeconds / answered.length : 0,
    },
    question_evaluations: evals,
    concept_performance: concepts,
    recommendations: [],
    ended_early: skipped > 0 && answered.length > 0,
  };
};
